import { useEffect, useMemo, useState } from "react";
import {
  Box, Stack, Typography, TextField, Button, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, Alert, CircularProgress, FormControlLabel, Switch, Tooltip,
  Checkbox, TableSortLabel, Collapse, AlertTitle, LinearProgress,
  Tabs, Tab, Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EditIcon from "@mui/icons-material/Edit";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SendIcon from "@mui/icons-material/Send";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  BillingItem, listBilling, sendToArca, getCbteFecha, fmtCbteFecha,
  type CbteFechaStatus,
  Receptor, listReceptors, getInvoicePdfUrl, sendInvoiceEmail,
  lookupArca, saveReceptor, downloadInvoicesZip,
  Emitter, listEmitters, refreshBillingCompanies,
} from "../services/billing";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import ReceptorEditDialog from "../components/ReceptorEditDialog";
import AmountEditDialog from "../components/AmountEditDialog";
import SendCopyEmailDialog from "../components/SendCopyEmailDialog";
import BillingCompanyDialog from "../components/BillingCompanyDialog";
import NewInvoiceDialog from "../components/NewInvoiceDialog";
import ArcaPreviewDialog from "../components/ArcaPreviewDialog";
import BillingByProcessor from "../components/BillingByProcessor";
import AddIcon from "@mui/icons-material/Add";

type SortColumn = "collector" | "cuit" | "condicion" | "email" | "invoice" | "amount" | "status" | "invoiced_at" | "cbte_fch";
type SortDir    = "asc" | "desc";
const rowKey = (it: BillingItem) => `${it.period}|${it.collector}`;
import { BRAND } from "../styles/theme";

function fmtMoney(v: number) {
  return v.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Clave ordenable (yyyyMMdd) de la fecha de emisión: cbte_fch, o la fecha de
 * invoiced_at para filas anteriores a la fecha retroactiva. */
function emissionDateKey(it: BillingItem): string {
  if (it.cbte_fch) return it.cbte_fch;
  if (!it.invoiced_at) return "";
  const d = new Date(it.invoiced_at);
  if (isNaN(d.getTime())) return "";
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/**
 * True si la fila ya facturada tiene un `billing_amount` distinto del
 * `invoiced_amount` congelado al emitir — i.e. el proceso de facturación
 * manual upstream pisó el monto después de que emitimos la factura.
 */
function amountWasOverwritten(it: BillingItem): boolean {
  return (
    it.status === "APPROVED" &&
    it.invoiced_amount != null &&
    Math.abs(it.billing_amount - it.invoiced_amount) > 0.005
  );
}
function amountChangeTitle(it: BillingItem): string {
  const when = it.datetime ? fmtDate(it.datetime) : "fecha desconocida";
  return (
    `Facturado por $${fmtMoney(it.invoiced_amount ?? 0)} (monto del comprobante en AFIP). ` +
    `El proceso de facturación manual escribió $${fmtMoney(it.billing_amount)} el ${when}, ` +
    `después de la emisión; se conserva como referencia pero no es lo facturado.`
  );
}

/** Fila (o grupo por CUIT) sin nada pendiente de emitir. */
function fullyApproved(it: BillingItem): boolean {
  return it.pending_amount !== undefined ? it.pending_amount <= 0 : it.status === "APPROVED";
}

/**
 * Monto que se MUESTRA y se SUMA como facturado: lo congelado al emitir
 * (`invoiced_amount`, = AFIP), no `billing_amount`, que el proceso manual
 * upstream puede pisar después (CP-436). En grupos por CUIT el backend ya suma
 * `invoiced_amount` de las filas aprobadas, así que vale también para PARTIAL.
 */
function approvedAmount(it: BillingItem): number {
  if (it.invoiced_amount != null) return it.invoiced_amount;
  if (it.pending_amount !== undefined) return (it.billing_amount || 0) - it.pending_amount;
  return it.status === "APPROVED" ? (it.billing_amount || 0) : 0;
}
function shownAmount(it: BillingItem): number {
  return fullyApproved(it) ? approvedAmount(it) : (it.billing_amount || 0);
}

/**
 * Month picker state: "YYYY-MM" (same format as <input type="month">).
 * Defaults to the previous calendar month.
 */
function defaultMonthYYYYMM(): string {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;
}

/** Convert "YYYY-MM" to the invoice_ar period 'YYYYMMDD-YYYYMMDD' (exclusive end). */
function monthToPeriod(yyyymm: string): { from: string; to: string; period: string } {
  const [y, m] = yyyymm.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const firstNext = new Date(y, m, 1);
  const fmt = (d: Date) =>
    `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
  const from = fmt(first);
  const to   = fmt(firstNext);
  return { from, to, period: `${from}-${to}` };
}

/** "YYYY-MM" → "DD/MM/YYYY → DD/MM/YYYY" for display. */
function humanRange(yyyymm: string): string {
  const [y, m] = yyyymm.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const last  = new Date(y, m,     0);
  const fmt = (d: Date) =>
    `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  return `${fmt(first)} → ${fmt(last)}`;
}

function StatusChip({ status, title }: { status: BillingItem["status"]; title?: string }) {
  // null/ausente → PENDIENTE: el proceso manual upstream dejó de escribir PENDING.
  const s = status ?? "PENDING";
  const colors: Record<string, [string, string]> = {
    PENDING:  [BRAND.pendingText, BRAND.pendingBg],
    PREPARED: ["#9A6700",          "#FFF6E0"],
    EMITTING: ["#7E22CE",          "#F3E8FF"],
    TIMEOUT:  ["#9A6700",          "#FEF3C7"],
    SENT:     ["#1F6FDE",          "#E0ECFF"],
    APPROVED: ["#166534",          "#DCFCE7"],
    PARTIAL:  ["#9A3412",          "#FFEDD5"],
    REJECTED: ["#991B1B",          "#FEE2E2"],
  };
  const label = s === "PENDING" ? "PENDIENTE" : s;
  const [fg, bg] = colors[s] ?? ["#333", "#EEE"];
  const chip = (
    <Chip
      label={label}
      size="small"
      sx={{
        fontWeight: 700, fontSize: 11, height: 26, borderRadius: 3,
        color: fg, bgcolor: bg,
      }}
    />
  );
  return title ? <Tooltip title={title}><span>{chip}</span></Tooltip> : chip;
}

export default function Billing() {
  const [month, setMonth] = useState<string>(defaultMonthYYYYMM());  // "YYYY-MM"
  // Importe mínimo a mostrar (editable). Default 1 — excluye filas de importe 0 /
  // centavos (cuentas sin actividad / tests). 0 muestra todo.
  const [minAmountStr, setMinAmountStr] = useState("1");
  const minAmount = Math.max(0, parseFloat(minAmountStr) || 0);
  const [distinctCuit, setDistinctCuit] = useState(true);    // agrupar por CUIT por default (matches excel view)
  const [items, setItems] = useState<BillingItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [receptors, setReceptors] = useState<Record<string, Receptor>>({});
  const [editing, setEditing] = useState<BillingItem | null>(null);
  const [editingAmount, setEditingAmount] = useState<BillingItem | null>(null);
  const [editingCompany, setEditingCompany] = useState<BillingItem | null>(null);
  // Fecha del comprobante del período (la que va a llevar la factura en AFIP).
  const [cbteFecha, setCbteFecha] = useState<CbteFechaStatus | null>(null);
  // Fecha elegida a mano. Solo se habilita cuando cbteFecha.editable === true.
  const [cbteFchManual, setCbteFchManual] = useState<string>("");
  const [newInvoiceOpen, setNewInvoiceOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmEmit, setConfirmEmit] = useState(false);
  const [bulkBusy,     setBulkBusy]     = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{label: string; done: number; total: number; current?: string} | null>(null);
  const [bulkErrors,   setBulkErrors]   = useState<Array<{cuit?: string; collector?: string; reason: string}>>([]);
  const [errorsOpen,   setErrorsOpen]   = useState(false);
  const [sortCol, setSortCol] = useState<SortColumn>("amount");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  // Multi-empresa: lista de emisores soportados (se carga una vez al montar)
  // y CUIT del emisor seleccionado actualmente. Si la API falla, queda vacío
  // y el botón de emitir lo deshabilita.
  const [emitters, setEmitters] = useState<Emitter[]>([]);
  const [emitterCuit, setEmitterCuit] = useState<number | null>(null);

  async function load() {
    setLoading(true); setErr(null);
    try {
      const { period } = monthToPeriod(month);
      const [d, rmap] = await Promise.all([
        listBilling({
          period,
          minAmount:     minAmount,
          distinctCuit:  distinctCuit,
          // Pasamos el emisor seleccionado: el backend filtra las APPROVED
          // por emitter_cuit y las pendientes por billing_company (cada
          // pendiente es facturable por UNA sola empresa).
          emitterCuit:   emitterCuit ?? undefined,
        }),
        listReceptors(),
      ]);
      setItems(d.items);
      setReceptors(rmap);
    } catch (e: any) {
      setErr(e.message ?? "Failed to load");
    } finally { setLoading(false); }

    // La fecha del comprobante va aparte: depende del último autorizado en AFIP
    // y de cuántas facturas quedan por emitir, así que se recalcula en cada
    // carga (después de emitir, el `pending` baja y puede destrabarse).
    try {
      const st = await getCbteFecha(monthToPeriod(month).period, emitterCuit ?? undefined);
      setCbteFecha(st);
      setCbteFchManual(st.editable ? st.fecha : "");
    } catch {
      // No romper la pantalla si AFIP no responde: la fecha se resuelve igual
      // al emitir, esto es solo la vista previa.
      setCbteFecha(null);
    }
  }

  function isComplete(r?: Receptor): boolean {
    return !!r && !!r.condicion_iva_id && !!r.actividad && !!r.email;
  }

  function handleSort(col: SortColumn) {
    if (col === sortCol) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  }

  // CP-125: facturas manuales recién cuando la facturación estándar (las rows
  // del archivo upstream) de la pestaña esté 100% emitida — si no, un
  // comprobante con fecha actual bloquea la fecha retroactiva del batch.
  const pendingStandard = useMemo(
    () => items.filter((i) => !i.manual && i.status !== "APPROVED" && !i.cae).length,
    [items],
  );
  // ...salvo que ya haya vencido la retroactiva (los primeros 10 días del mes
  // siguiente al cierre): ahí se habilitan igual, porque lo único que el gate
  // protegía era esa fecha retroactiva. Alcanza con cualquiera de las dos.
  // El plazo NO se calcula acá: sale del mismo `cbteFecha` que manda el backend
  // (days_late / retro_max_days), así no hay dos "día 10" que puedan divergir.
  const retroVencida = cbteFecha != null && cbteFecha.days_late != null
    && cbteFecha.days_late > cbteFecha.retro_max_days;
  // Ya NO bloquea la "Nueva factura": mientras el batch estándar tenga pendientes y
  // no haya vencido la retroactiva, la manual se emite con la fecha RETROACTIVA del
  // período (último día del mes facturado) — no rompe la monotonía del talonario.
  // Vencida la retroactiva o sin pendientes → fecha actual / editable.
  const manualRetroForzado = pendingStandard > 0 && !retroVencida;

  async function onRefreshCompanies() {
    setBulkBusy(true); setErr(null);
    try {
      const { period } = monthToPeriod(month);
      const r = await refreshBillingCompanies(period);
      setMsg(r.updated
        ? `Empresas refrescadas desde la BD: ${r.updated} fila(s) actualizada(s) de ${r.checked} pendientes.`
        : `Empresas al día: ${r.checked} fila(s) pendientes verificadas, sin cambios.`);
      await load();
    } catch (e: any) {
      setErr(e.message ?? "No se pudo refrescar billing_company");
    } finally { setBulkBusy(false); }
  }

  const sortedItems = useMemo(() => {
    const arr = [...items];
    const dir = sortDir === "asc" ? 1 : -1;
    const getKey = (it: BillingItem) => {
      const r = receptors[it.cuit];
      switch (sortCol) {
        case "collector": return (it.collector ?? "").toLowerCase();
        case "cuit":      return it.cuit ?? "";
        case "condicion": return r?.condicion_iva_id ?? 0;
        case "email":     return (r?.email ?? "").toLowerCase();
        case "invoice":     return it.invoice_number ?? "";
        case "amount":      return shownAmount(it);
        case "status":      return it.status ?? "";
        case "invoiced_at": return it.invoiced_at ?? "";
        case "cbte_fch":    return emissionDateKey(it);
      }
    };
    return arr.sort((a, b) => {
      const ka = getKey(a); const kb = getKey(b);
      if (ka === kb) return 0;
      return (ka > kb ? 1 : -1) * dir;
    });
  }, [items, receptors, sortCol, sortDir]);

  // PARTIAL rows can still be billed (their PENDING peers haven't been emitted).
  const selectableItems = sortedItems.filter((it) => it.status !== "APPROVED");
  const billableAmount = (it: BillingItem) =>
    it.pending_amount !== undefined ? it.pending_amount : it.billing_amount;
  const allSelected     = selected.size > 0 && selected.size === selectableItems.length;
  const someSelected    = selected.size > 0 && selected.size <  selectableItems.length;

  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else             setSelected(new Set(selectableItems.map(rowKey)));
  }
  function toggleOne(it: BillingItem) {
    const k = rowKey(it);
    const next = new Set(selected);
    if (next.has(k)) next.delete(k); else next.add(k);
    setSelected(next);
  }

  /**
   * Emit invoices in chunks of 5 (Lambda hard cap). Sequential between chunks
   * — each /billing/send-to-arca call hits AFIP per row, and AFIP enforces
   * sequential CbteNro, so parallel chunks would just race for the same
   * number and most would fail with err 10016.
   */
  async function onSendSelected(idsToSend: string[], expected: Record<string, string>) {
    // `idsToSend` y `expected` vienen de la previsualización del servidor
    // (ArcaPreviewDialog): sólo los comprobantes que se vieron OK, con su
    // huella. El backend no emite el que cambió desde que se mostró.
    setMsg(null); setErr(null); setBulkErrors([]); setErrorsOpen(false);
    const ids = idsToSend;
    if (!ids.length) return;

    const chunkSize = 5;
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += chunkSize) chunks.push(ids.slice(i, i + chunkSize));

    setBulkBusy(true);
    setBulkProgress({ label: "Emitiendo facturas", done: 0, total: ids.length });

    let approvedTotal = 0;
    const errs: Array<{cuit?: string; collector?: string; reason: string}> = [];
    let processed = 0;

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      setBulkProgress({
        label:   "Emitiendo facturas",
        done:    processed,
        total:   ids.length,
        current: `Tanda ${i + 1}/${chunks.length} — ${chunk.length} factura${chunk.length === 1 ? "" : "s"}`,
      });
      try {
        // La fecha manual solo se manda si el período la tiene destrabada; si no,
        // el backend responde 409 (y hace bien: la revalida él, no confía en la UI).
        const r = await sendToArca(
          chunk, distinctCuit, emitterCuit ?? undefined,
          cbteFecha?.editable && cbteFchManual ? cbteFchManual : undefined,
          Object.fromEntries(chunk.filter((id) => expected[id]).map((id) => [id, expected[id]])));
        approvedTotal += r.approved;
        for (const res of r.results) {
          if (!res.ok) {
            const it = sortedItems.find((x) => rowKey(x) === res.id);
            errs.push({
              collector: it?.collector,
              cuit:      it?.cuit,
              reason:    res.error || "rechazada por AFIP",
            });
          }
        }
      } catch (e: any) {
        // Whole chunk blew up (e.g. WSAA login dead). Mark each id failed.
        for (const id of chunk) {
          const it = sortedItems.find((x) => rowKey(x) === id);
          errs.push({
            collector: it?.collector,
            cuit:      it?.cuit,
            reason:    `Tanda ${i + 1}: ${e.message || "error"}`,
          });
        }
      }
      processed += chunk.length;
    }

    setBulkProgress({ label: "Emitiendo facturas", done: ids.length, total: ids.length });
    setBulkBusy(false);
    setBulkErrors(errs);
    setSelected(new Set());

    const parts = [
      approvedTotal ? `${approvedTotal} factura(s) emitida(s) con CAE` : null,
      errs.length   ? `${errs.length} rechazada(s)` : null,
    ].filter(Boolean);
    setMsg(parts.join(" · ") || "Sin cambios");
    await load();
  }

  async function onOpenPdf(it: BillingItem) {
    setMsg(null); setErr(null);
    try {
      const url = await getInvoicePdfUrl(it.period, it.collector);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e: any) { setErr(e.message ?? "No se pudo generar el PDF"); }
  }

  /**
   * Completa los datos de facturación de los receptores seleccionados desde el
   * padrón oficial de ARCA: razón social, condición frente al IVA y domicilio.
   * Secuencial a propósito (el padrón corta si se lo golpea en paralelo).
   * Actualiza `receptors` por CUIT para que la fila se refresque en vivo.
   *
   * Lo ya cargado NUNCA se pisa: el padrón completa huecos, no corrige lo que
   * el operador puso a mano. Por eso se saltea solo si ya está todo.
   *
   * Los errores se juntan y se muestran en un Alert colapsable al final.
   */
  async function onBulkFetchRazonSocial() {
    setMsg(null); setErr(null); setBulkErrors([]); setErrorsOpen(false);
    const ids = Array.from(selected);
    if (!ids.length) return;

    // Dedup CUITs upfront. Each CUIT carries the collector name of the first
    // row we saw it in so error messages can identify the company.
    const seen = new Map<string, string>();   // cuit → collector
    for (const id of ids) {
      const it = sortedItems.find((x) => rowKey(x) === id);
      if (it && !seen.has(it.cuit)) seen.set(it.cuit, it.collector);
    }
    const targets = Array.from(seen.entries());

    setBulkBusy(true);
    setBulkProgress({ label: "Consultando padrón ARCA", done: 0, total: targets.length });

    let updated = 0, alreadyHad = 0;
    const errs: Array<{cuit?: string; collector?: string; reason: string}> = [];

    for (let i = 0; i < targets.length; i++) {
      const [cuit, collector] = targets[i];
      setBulkProgress({ label: "Consultando padrón ARCA", done: i, total: targets.length, current: `${collector} (${cuit})` });

      // Read the latest receptor from state — could have been changed manually
      // during the loop. Functional setState below uses the most recent value.
      let existing: Receptor | undefined;
      setReceptors((prev) => { existing = prev[cuit]; return prev; });

      // Ya está completo (incluida la provincia para IIBB): no gastamos una
      // consulta al padrón.
      if (existing?.razon_social && existing?.condicion_iva_id && existing?.domicilio && existing?.provincia) {
        alreadyHad++;
        continue;
      }

      try {
        const lookup = await lookupArca(cuit);
        if (!lookup.razon_social) {
          errs.push({ cuit, collector, reason: "Sin razón social en la respuesta" });
          continue;
        }
        // OJO: el PUT reescribe el item completo (los campos ausentes quedan en
        // vacío), así que hay que mandar TODOS — incluido `domicilio`, que antes
        // no se enviaba y se borraba en cada corrida.
        const merged: Partial<Receptor> = {
          razon_social:       existing?.razon_social       || lookup.razon_social,
          condicion_iva_id:   existing?.condicion_iva_id   ?? lookup.condicion_iva_id   ?? undefined,
          condicion_iva_desc: existing?.condicion_iva_desc || lookup.condicion_iva_desc || "",
          domicilio:          existing?.domicilio          || lookup.domicilio          || "",
          provincia:          existing?.provincia          || lookup.provincia          || "",
          actividad:          existing?.actividad ?? undefined,
          email:              existing?.email     || "",
        };
        const saved = await saveReceptor(cuit, merged);
        // Live update — the row in the table re-renders immediately.
        setReceptors((prev) => ({ ...prev, [cuit]: saved }));
        updated++;
      } catch (e: any) {
        errs.push({ cuit, collector, reason: e.message || "error desconocido" });
      }
    }

    setBulkProgress({ label: "Consultando padrón ARCA", done: targets.length, total: targets.length });
    setBulkBusy(false);
    setBulkErrors(errs);

    const parts = [
      updated    ? `${updated} completadas desde ARCA` : null,
      alreadyHad ? `${alreadyHad} ya estaban completas` : null,
      errs.length ? `${errs.length} fallaron` : null,
    ].filter(Boolean);
    setMsg(parts.join(" · ") || "Sin cambios");
  }

  async function onDownloadZip() {
    setMsg(null); setErr(null);
    // Download PDFs for selected APPROVED rows. If nothing selected, default
    // to all currently visible APPROVED rows in the listing.
    let target: BillingItem[];
    if (selected.size) {
      target = sortedItems.filter((it) => selected.has(rowKey(it)) && it.status === "APPROVED");
    } else {
      target = sortedItems.filter((it) => it.status === "APPROVED");
    }
    if (!target.length) {
      setErr("No hay facturas APPROVED para descargar (seleccioná filas APPROVED o desactivá los filtros).");
      return;
    }
    setBulkBusy(true);
    setBulkProgress({ label: `Generando ZIP de ${target.length} PDFs`, done: 0, total: 1 });
    try {
      const url = await downloadInvoicesZip(target.map(rowKey));
      const a = document.createElement("a");
      a.href = url;
      a.download = `facturas-${month}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setMsg(`ZIP con ${target.length} PDFs descargado`);
    } catch (e: any) { setErr(e.message ?? "ZIP failed"); }
    finally { setBulkBusy(false); setBulkProgress(null); }
  }

  async function onSendEmail(it: BillingItem) {
    setMsg(null); setErr(null);
    try {
      const r = await sendInvoiceEmail(it.period, it.collector);
      setMsg(`Mail enviado a ${r.recipient} (${r.subject})`);
    } catch (e: any) { setErr(e.message ?? "No se pudo enviar el mail"); }
  }

  // CP-157: copia del PDF de una factura emitida a un email agregado a posteriori.
  const [copyFor, setCopyFor] = useState<BillingItem | null>(null);

  // Auto-reload when any filter changes — no manual refresh button.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [month, minAmount, distinctCuit, emitterCuit]);

  useEffect(() => {
    listEmitters()
      .then((r) => { setEmitters(r.emitters); setEmitterCuit(r.default_cuit); })
      .catch((e) => setErr(`No se pudo cargar el listado de emisores: ${e.message ?? e}`));
  }, []);

  return (
    <Box>
      <Box sx={{ p: 4 }}>
        {/* Solapas de Emisor — prominentes arriba para que sea imposible
            equivocarse de empresa al emitir. */}
        {emitters.length > 0 && (
          <Box sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              Emisor
            </Typography>
            <Tabs
              value={emitterCuit ?? false}
              onChange={(_, v) => !bulkBusy && setEmitterCuit(v)}
              textColor="primary"
              indicatorColor="primary"
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 15,
                  minHeight: 48,
                },
              }}
            >
              {emitters.map((em) => (
                <Tab
                  key={em.cuit}
                  value={em.cuit}
                  disabled={bulkBusy}
                  label={
                    <Stack direction="row" spacing={1} alignItems="baseline">
                      <span>{em.razon_social}</span>
                      <Typography variant="caption" color="text.secondary">
                        CUIT {em.cuit}
                      </Typography>
                    </Stack>
                  }
                />
              ))}
            </Tabs>
          </Box>
        )}

        {/* Filter + action bar — `alignItems="flex-start"` para que la fecha
            que va abajo del Período no desalinee a los botones (todos
            quedan pegados al borde superior). */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          mb={3}
          alignItems={{ xs: "stretch", md: "flex-start" }}
          flexWrap="wrap"
          useFlexGap
        >
          {/* Período + sub-caption con fechas */}
          <Stack spacing={0.5} sx={{ width: 260 }}>
            <TextField
              type="month"
              size="small"
              label="Período a facturar"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={bulkBusy}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ pl: 1.5 }}>
              {humanRange(month)}{loading && " · cargando..."}
            </Typography>
          </Stack>
          <Tooltip title={manualRetroForzado
            ? `La facturación estándar del período tiene ${pendingStandard} factura(s) sin emitir: la factura manual se va a emitir con fecha RETROACTIVA ${cbteFecha ? fmtCbteFecha(cbteFecha.fecha) : "del cierre del período"} (último día del período). Cuando se emitan todas, o pasen los primeros ${cbteFecha?.retro_max_days ?? 10} días del mes siguiente, se podrá usar fecha actual/editable.`
            : "Crear una factura manual (cliente/monto que no vienen del archivo). La empresa emisora la determina la BD."}>
            <span>
              <Button
                variant="outlined"
                size="medium"
                startIcon={<AddIcon />}
                sx={{ borderColor: BRAND.primary, color: BRAND.primary, borderRadius: 999, height: 40 }}
                onClick={() => setNewInvoiceOpen(true)}
                disabled={bulkBusy}
              >
                Nueva factura
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Re-lee billing_company desde la BD (collection_config) y actualiza las filas pendientes del período. Usar si cambió la empresa de algún colector.">
            <span>
              <Button
                variant="outlined"
                size="medium"
                sx={{ borderColor: BRAND.primary, color: BRAND.primary, borderRadius: 999, height: 40 }}
                onClick={onRefreshCompanies}
                disabled={bulkBusy}
              >
                Empresas ⟳
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            size="medium"
            startIcon={bulkBusy ? <CircularProgress size={14} /> : undefined}
            sx={{ borderColor: BRAND.primary, color: BRAND.primary, borderRadius: 999, height: 40 }}
            onClick={onBulkFetchRazonSocial}
            disabled={!selected.size || bulkBusy}
            title="Completa Razón Social, Condición frente al IVA y Domicilio desde el padrón oficial de ARCA. No pisa lo ya cargado."
          >
            Completar datos desde ARCA
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            startIcon={bulkBusy ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
            sx={{ borderRadius: 999, height: 40 }}
            onClick={() => setConfirmEmit(true)}
            disabled={!selected.size || bulkBusy || !emitterCuit}
          >
            Emitir {selected.size || ""} factura{selected.size === 1 ? "" : "s"}
          </Button>
          <Tooltip title="Descarga todos los comprobantes como PDFs zipeados">
            <span>
              <Button
                variant="outlined"
                size="medium"
                startIcon={<FileDownloadOutlinedIcon />}
                sx={{ borderColor: BRAND.primary, color: BRAND.primary, borderRadius: 999, height: 40 }}
                onClick={onDownloadZip}
                disabled={bulkBusy}
              >
                Comprobantes
              </Button>
            </span>
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={3} mb={2} alignItems="center">
          <Tooltip title="Muestra solo filas con importe ≥ este valor. 0 muestra todo. Default 1 (excluye importes 0 / centavos de cuentas sin actividad).">
            <TextField
              type="number"
              size="small"
              label="Importe mínimo ≥"
              value={minAmountStr}
              onChange={(e) => setMinAmountStr(e.target.value)}
              disabled={bulkBusy}
              inputProps={{ min: 0, step: "any" }}
              sx={{ width: 150, "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
            />
          </Tooltip>
          <Tooltip title="Colapsa varias filas con el mismo CUIT en una sola (suma importes). Ej.: AutosurRioGrande1 + AutosurRioGrande5 → un solo CUIT">
            <FormControlLabel
              control={<Switch checked={distinctCuit} onChange={(e) => setDistinctCuit(e.target.checked)} color="primary" disabled={bulkBusy} />}
              label="Agrupar por CUIT"
            />
          </Tooltip>
          {selected.size > 0 && (() => {
            const selectedTotal = sortedItems
              .filter((it) => selected.has(rowKey(it)))
              .reduce((s, it) => s + billableAmount(it), 0);
            return (
              <Chip
                label={`${selected.size} seleccionada${selected.size === 1 ? "" : "s"} · $${fmtMoney(selectedTotal)} a facturar`}
                onDelete={() => setSelected(new Set())}
                sx={{
                  fontWeight: 700,
                  bgcolor: BRAND.primaryLight,
                  color: BRAND.primary,
                  borderRadius: 2,
                }}
              />
            );
          })()}
        </Stack>

        {bulkBusy && bulkProgress && (
          <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Typography variant="body2">
                {bulkProgress.label} — {bulkProgress.done} de {bulkProgress.total}
                {bulkProgress.current && ` · ${bulkProgress.current}`}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={bulkProgress.total ? (bulkProgress.done / bulkProgress.total) * 100 : 0}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          </Alert>
        )}

        {err && <Alert severity="error"   sx={{ mb: 2 }}>{err}</Alert>}
        {msg && <Alert severity="success" sx={{ mb: 2 }}>{msg}</Alert>}

        {bulkErrors.length > 0 && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <IconButton size="small" onClick={() => setErrorsOpen((o) => !o)}>
                {errorsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            }
            onClick={() => setErrorsOpen((o) => !o)}
            style={{ cursor: "pointer" }}
          >
            <AlertTitle sx={{ mb: 0 }}>
              {bulkErrors.length} {bulkErrors.length === 1 ? "error" : "errores"}
            </AlertTitle>
            <Collapse in={errorsOpen}>
              <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5, fontSize: 13 }}>
                {bulkErrors.map((e, i) => (
                  <li key={i}>
                    <b>{e.collector || "—"}</b>{e.cuit ? ` (${e.cuit})` : ""}: {e.reason}
                  </li>
                ))}
              </Box>
            </Collapse>
          </Alert>
        )}

        {(() => {
          // Amounts: invariant across grouping (real $ facturado/pendiente).
          // Items: count one per VISIBLE row. PARTIAL (grouped row with mixed
          // peers) counts as "pendiente" because there's still work left.
          const stats = items.reduce(
            (acc, it) => {
              // amounts
              // Facturado = lo congelado al emitir (AFIP), no el monto del upstream.
              if (it.pending_amount !== undefined) {
                acc.totalPending  += it.pending_amount;
                acc.totalApproved += approvedAmount(it);
              } else if (it.status === "APPROVED") {
                acc.totalApproved += approvedAmount(it);
              } else {
                acc.totalPending  += it.billing_amount || 0;
              }
              // items (one per visible row)
              const stillToBill =
                (it.pending_amount !== undefined && it.pending_amount > 0) ||
                (it.pending_amount === undefined && it.status !== "APPROVED");
              if (stillToBill) acc.itemsPending  += 1;
              else             acc.itemsApproved += 1;
              return acc;
            },
            { totalApproved: 0, totalPending: 0, itemsApproved: 0, itemsPending: 0 },
          );
          const totalItems  = stats.itemsApproved + stats.itemsPending;
          const totalAmount = stats.totalApproved + stats.totalPending;
          const pctApproved = totalAmount ? (stats.totalApproved / totalAmount) * 100 : 0;
          return (
            <Box sx={{ bgcolor: BRAND.primaryLight, borderRadius: 2, px: 3, py: 2, mb: 2 }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems="stretch">
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1, mb: 0.5 }}>
                    Items
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="baseline">
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: BRAND.primary }}>{totalItems}</Typography>
                      <Typography variant="caption" color="text.secondary">total</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: "#166534" }}>{stats.itemsApproved}</Typography>
                      <Typography variant="caption" sx={{ color: "#166534" }}>facturados</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: "#9A3412" }}>{stats.itemsPending}</Typography>
                      <Typography variant="caption" sx={{ color: "#9A3412" }}>pendientes</Typography>
                    </Box>
                  </Stack>
                </Box>
                <Box sx={{ flex: 2, textAlign: "right" }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1, mb: 0.5 }}>
                    Total c/IVA
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: BRAND.primary }}>
                    ${fmtMoney(totalAmount)}
                  </Typography>
                  <Stack direction="row" spacing={3} justifyContent="flex-end" mt={0.5}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#166534" }}>
                        ${fmtMoney(stats.totalApproved)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#166534" }}>facturado ({pctApproved.toFixed(1)}%)</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#9A3412" }}>
                        ${fmtMoney(stats.totalPending)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#9A3412" }}>pendiente</Typography>
                    </Box>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={pctApproved}
                    sx={{
                      mt: 1, height: 6, borderRadius: 3,
                      bgcolor: "#FFEDD5",
                      "& .MuiLinearProgress-bar": { bgcolor: "#166534" },
                    }}
                  />
                </Box>
              </Stack>
            </Box>
          );
        })()}

        <Accordion variant="outlined" TransitionProps={{ unmountOnExit: true }}
          sx={{ mb: 2, borderRadius: 2, "&:before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 700 }}>Facturación por procesador y cuenta</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <BillingByProcessor period={monthToPeriod(month).period} />
          </AccordionDetails>
        </Accordion>

        <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: "auto" }}>
          <Table sx={{ minWidth: 1200 }}>
            <TableHead sx={{ bgcolor: BRAND.primaryLight }}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                    color="primary"
                  />
                </TableCell>
                {([
                  ["invoiced_at","Fecha facturación",        "left"],
                  ["cbte_fch",   "Fecha de emisión",          "left"],
                  ["collector", "Colector / Razón Social",   "left"],
                  ["cuit",      "CUIT",                      "left"],
                  ["condicion", "Cond. IVA",                 "left"],
                  ["email",     "Email",                     "left"],
                  ["invoice",   "Invoice Number",            "left"],
                  ["amount",    "Total c/IVA",               "right"],
                  ["status",    "Status",                    "left"],
                ] as Array<[SortColumn, string, "left" | "right"]>).map(([col, lbl, align]) => (
                  <TableCell
                    key={col}
                    align={align}
                    sx={{ fontWeight: 700 }}
                    sortDirection={sortCol === col ? sortDir : false}
                  >
                    <TableSortLabel
                      active={sortCol === col}
                      direction={sortCol === col ? sortDir : "asc"}
                      onClick={() => handleSort(col)}
                    >
                      {lbl}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedItems.map((it, idx) => {
                const r = receptors[it.cuit];
                const complete = isComplete(r);
                const rowBg = complete ? undefined : "#FFF4E5";
                const k = rowKey(it);
                const isSelected = selected.has(k);
                return (
                  <TableRow
                    key={`${it.cuit}-${idx}`}
                    hover
                    selected={isSelected}
                    sx={{ "& td": { bgcolor: rowBg } }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleOne(it)}
                        color="primary"
                        disabled={it.status === "APPROVED" || billableAmount(it) <= 0}
                      />
                    </TableCell>
                    <TableCell>
                      {it.invoiced_at
                        ? fmtDate(it.invoiced_at)
                        : cbteFecha
                          // Todavía no emitida: se muestra la fecha que VA a
                          // llevar el comprobante, que es el dato que se pidió
                          // tener a la vista antes de facturar.
                          ? <Tooltip title={
                              (cbteFchManual && cbteFecha.editable
                                ? "Fecha elegida a mano para esta emisión. "
                                : cbteFecha.retroactiva
                                  ? `Retroactiva al cierre del período (hasta ${cbteFecha.retro_max_days} días después). `
                                  : "Fuera del plazo de retroactividad: se emite con la fecha de hoy. ") +
                              (cbteFecha.verified ? "" : "No se pudo verificar contra AFIP.")
                            }>
                              <Typography variant="body2" sx={{
                                color: cbteFecha.verified ? "text.primary" : "warning.main",
                                fontStyle: "italic",
                              }}>
                                {fmtCbteFecha(
                                  cbteFecha.editable && cbteFchManual ? cbteFchManual : cbteFecha.fecha)}
                              </Typography>
                            </Tooltip>
                          : "—"}
                    </TableCell>
                    <TableCell>
                      {/* Fecha del comprobante en AFIP (CP-436, pedido de PGA): es la
                          que imprime el PDF y la que figura en el QR. Puede ser
                          retroactiva al cierre del período, por eso difiere de
                          "Fecha facturación" (cuándo se emitió desde WebAdmin). */}
                      {it.cbte_fch
                        ? fmtCbteFecha(it.cbte_fch)
                        : it.invoiced_at
                          ? <Tooltip title="Emitida antes de la fecha retroactiva (ago-2026): el comprobante lleva la fecha en que se facturó desde WebAdmin.">
                              <span>{fmtDate(it.invoiced_at)}</span>
                            </Tooltip>
                          : "—"}
                    </TableCell>
                    <TableCell>
                      <Box>{it.collector}</Box>
                      {r?.razon_social && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {r.razon_social}
                        </Typography>
                      )}
                      {/* Empresa que factura. Editable solo mientras no esté
                          emitida: con CAE el emisor ya quedó fijo en AFIP. */}
                      <Tooltip title={
                        it.status === "APPROVED" || it.cae
                          ? "Ya emitida: el emisor quedó fijo en el comprobante de AFIP"
                          : it.billing_company_manual
                            ? "Empresa cambiada a mano — 'Empresas ⟳' no la va a pisar. Clic para cambiarla."
                            : "Empresa según la BD. Clic para cambiarla solo en este período."
                      }>
                        <Chip
                          size="small"
                          label={it.billing_company || "B2BBNPL S.A."}
                          onClick={
                            it.status === "APPROVED" || it.cae
                              ? undefined
                              : () => setEditingCompany(it)
                          }
                          icon={it.billing_company_manual ? <EditIcon sx={{ fontSize: 12 }} /> : undefined}
                          sx={{
                            mt: 0.5, height: 18, fontSize: 10,
                            cursor: it.status === "APPROVED" || it.cae ? "default" : "pointer",
                            bgcolor: it.billing_company_manual ? "#EDE9FE" : undefined,
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>{it.cuit}</TableCell>
                    <TableCell>
                      {r?.condicion_iva_id
                        ? `${r.condicion_iva_id} · ${r.condicion_iva_desc}`
                        : <Typography component="span" variant="caption" color="warning.main">
                            <WarningAmberIcon fontSize="inherit" sx={{ verticalAlign: "middle", mr: 0.5 }} />
                            faltante
                          </Typography>}
                    </TableCell>
                    <TableCell>
                      {r?.email
                        ? (() => {
                            // Puede haber varios destinatarios: se muestra el
                            // primero y "+N" para no ensanchar la tabla.
                            const dests = r.email.split(/[,;]/).map((e) => e.trim()).filter(Boolean);
                            return (
                              <Tooltip title={dests.length > 1 ? dests.join(", ") : ""}>
                                <Typography component="span" variant="body2" sx={{ fontSize: 12 }}>
                                  {dests[0]}
                                  {dests.length > 1 && (
                                    <Typography component="span" variant="caption"
                                                sx={{ ml: 0.5, color: "text.secondary", fontWeight: 700 }}>
                                      +{dests.length - 1}
                                    </Typography>
                                  )}
                                </Typography>
                              </Tooltip>
                            );
                          })()
                        : <Typography component="span" variant="caption" color="warning.main">
                            <WarningAmberIcon fontSize="inherit" sx={{ verticalAlign: "middle", mr: 0.5 }} />
                            faltante
                          </Typography>}
                    </TableCell>
                    <TableCell>
                      {it.invoice_number ?? "—"}
                    </TableCell>
                    <TableCell align="right">
                      {fullyApproved(it) ? (
                        // Ya facturada: lo que dice el comprobante en AFIP. Si el
                        // upstream pisó el monto después, queda abajo como
                        // referencia con el "*" (no se pierde, pero no es lo facturado).
                        <Box>
                          <Typography variant="body2" component="span">
                            {fmtMoney(approvedAmount(it))}
                          </Typography>
                          {amountWasOverwritten(it) && (
                            <Tooltip title={amountChangeTitle(it)}>
                              <Box component="span" sx={{ color: "warning.main", fontWeight: 900, ml: 0.5, cursor: "help" }}>
                                *
                              </Box>
                            </Tooltip>
                          )}
                          {amountWasOverwritten(it) && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                              upstream: {fmtMoney(it.billing_amount)}
                            </Typography>
                          )}
                        </Box>
                      ) : it.pending_amount !== undefined && it.pending_amount !== it.billing_amount ? (
                        <Box>
                          <Typography variant="body2" sx={{ color: "warning.dark", fontWeight: 700 }}>
                            {fmtMoney(it.pending_amount)} a facturar
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            de {fmtMoney(it.billing_amount)} total mes
                          </Typography>
                        </Box>
                      ) : (
                        fmtMoney(it.billing_amount)
                      )}
                    </TableCell>
                    <TableCell><StatusChip status={it.status} title={it.afip_message ?? undefined} /></TableCell>
                    <TableCell>
                      {it.status === "APPROVED" || (it.status === "PARTIAL" && billableAmount(it) <= 0) ? (
                        // Fully issued — only post-emission actions
                        <Stack direction="row" spacing={0}>
                          <Tooltip title="Enviar PDF por mail">
                            <IconButton size="small" onClick={() => onSendEmail(it)}>
                              <MailOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Enviar copia a otro email (se puede agregar al receptor)">
                            <IconButton size="small" onClick={() => setCopyFor(it)}>
                              <ForwardToInboxIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ver / descargar PDF">
                            <IconButton size="small" onClick={() => onOpenPdf(it)}>
                              <PictureAsPdfIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : (
                        // Pending — full edit + workflow actions
                        <Stack direction="row" spacing={0}>
                          <Tooltip title="Editar receptor">
                            <IconButton size="small" onClick={() => setEditing(it)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar monto">
                            <IconButton size="small" onClick={() => setEditingAmount(it)}>
                              <AttachMoneyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Emitir factura">
                            <span>
                              {/* Selecciona la fila y abre el diálogo de confirmación
                                  (mismo flujo que el bulk). Llamar onSendSelected()
                                  directo acá leía `selected` viejo del closure y
                                  emitía la selección anterior (o nada). */}
                              <IconButton
                                size="small"
                                disabled={bulkBusy || !emitterCuit}
                                onClick={() => {
                                  setSelected(new Set([rowKey(it)]));
                                  setConfirmEmit(true);
                                }}
                              >
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!sortedItems.length && !loading && (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    Sin items para facturar
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <ArcaPreviewDialog
        open={confirmEmit}
        onClose={() => setConfirmEmit(false)}
        ids={Array.from(selected)}
        grouped={distinctCuit}
        emitterCuit={emitterCuit}
        cbteFecha={cbteFecha}
        cbteFchManual={cbteFchManual}
        onCbteFchManual={setCbteFchManual}
        busy={bulkBusy}
        onConfirm={(ids, expected) => { setConfirmEmit(false); void onSendSelected(ids, expected); }}
      />

      <NewInvoiceDialog
        open={newInvoiceOpen}
        period={monthToPeriod(month).period}
        monthLabel={humanRange(month)}
        receptors={receptors}
        cbteFecha={cbteFecha}
        onClose={() => setNewInvoiceOpen(false)}
        onCreated={(item, receptor) => {
          if (receptor) setReceptors((prev) => ({ ...prev, [receptor.cuit]: receptor }));
          const empresa = item.billing_company || "B2BBNPL S.A.";
          setMsg(`Factura manual creada: ${item.collector} — $${fmtMoney(item.billing_amount)} (empresa según BD: ${empresa}). Completá el receptor si falta y emití normalmente.`);
          load();
        }}
      />

      <SendCopyEmailDialog
        open={!!copyFor}
        item={copyFor}
        onClose={() => setCopyFor(null)}
        onDone={(m, receptorUpdated) => {
          setCopyFor(null); setMsg(m);
          if (receptorUpdated) load();   // refresca la columna Email
        }}
      />

      <ReceptorEditDialog
        open={!!editing}
        cuit={editing?.cuit ?? ""}
        collector={editing?.collector}
        initial={editing ? receptors[editing.cuit] : undefined}
        onClose={() => setEditing(null)}
        onSaved={(r) => setReceptors({ ...receptors, [r.cuit]: r })}
      />

      <AmountEditDialog
        open={!!editingAmount}
        daterange={editingAmount?.period ?? ""}
        collector={editingAmount?.collector ?? ""}
        cuit={editingAmount?.cuit ?? ""}
        // Modo agrupado: lo editable es el total PENDIENTE del CUIT (lo que iría
        // a la factura agrupada), no la suma del mes que incluye lo ya emitido.
        initial={editingAmount
          ? (editingAmount.pending_amount !== undefined ? editingAmount.pending_amount : editingAmount.billing_amount)
          : 0}
        grouped={editingAmount?.pending_amount !== undefined}
        pendingCollectors={editingAmount?.pending_collectors}
        emitterCuit={emitterCuit}
        onClose={() => setEditingAmount(null)}
        onSaved={(next) => {
          // Merge back into items state — totals card recomputes from items.
          // Modo agrupado: el backend repartió `next.amount` entre las filas
          // pendientes del CUIT, así que pending_amount pasa a ser ese total y
          // billing_amount se mueve por el delta (lo aprobado no cambia).
          // Fila suelta: billing_amount = nuevo monto.
          setItems(items.map((it) =>
            editingAmount && rowKey(it) === rowKey(editingAmount)
              ? (it.pending_amount !== undefined
                  ? {
                      ...it,
                      billing_amount: it.billing_amount + (next.amount - it.pending_amount),
                      pending_amount: next.amount,
                    }
                  : { ...it, billing_amount: next.amount })
              : it
          ));
        }}
      />

      <BillingCompanyDialog
        open={!!editingCompany}
        period={editingCompany?.period ?? ""}
        collector={editingCompany?.collector ?? ""}
        cuit={editingCompany?.cuit ?? ""}
        current={editingCompany?.billing_company}
        wasManual={!!editingCompany?.billing_company_manual}
        emitters={emitters}
        onClose={() => setEditingCompany(null)}
        onSaved={(company) => {
          setItems(items.map((it) =>
            editingCompany && rowKey(it) === rowKey(editingCompany)
              ? { ...it, billing_company: company, billing_company_manual: true }
              : it
          ));
          // La fila puede caer fuera de la pestaña de emisor activa (el filtro
          // de pendientes es por billing_company): se recarga para que la lista
          // refleje dónde quedó y no muestre una fila que ya no le corresponde.
          load();
        }}
      />
    </Box>
  );
}
